import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useResilientMutation } from '@/lib/use-resilient-mutation';
import { normalizeQueryFilter } from '@/lib/normalize-query-filter';
import { listCacheFallback } from '@/lib/list-cache-fallback';
import type { Client, ClientFilters, CreateClientInput, UpdateClientInput } from '@erp/data-access/interfaces';

const CLIENTS_KEY = ['clients'] as const;

export function useClients(filters?: ClientFilters) {
  const ds = useDataService();
  const normalized = normalizeQueryFilter(filters);
  return useQuery({
    queryKey: [...CLIENTS_KEY, normalized],
    queryFn: () => ds.clients.list(normalized),
  });
}

/** Aperçu du prochain code client. `enabled`: typiquement true uniquement en mode création. */
export function useNextClientCode(enabled: boolean) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...CLIENTS_KEY, 'next-code'],
    queryFn: () => ds.clients.peekNextCode(),
    enabled,
    staleTime: 0,
  });
}

export function useClient(id: string) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...CLIENTS_KEY, id],
    queryFn: () => ds.clients.getById(id),
    enabled: !!id,
    placeholderData: () => listCacheFallback<Client>(qc, CLIENTS_KEY, id),
  });
}

const nowIso = () => new Date().toISOString();
const OFFLINE_PREFIX = 'OFFLINE-';

export function useCreateClient() {
  const ds = useDataService();
  return useResilientMutation<CreateClientInput, Client>({
    entity: 'clients',
    op: 'create',
    invalidate: [CLIENTS_KEY],
    online: (input) => ds.clients.create(input),
    optimistic: (input, clientId) => ({
      id: clientId,
      code: OFFLINE_PREFIX + clientId.slice(0, 6).toUpperCase(),
      name: input.name,
      type: input.type,
      taxId: input.taxId ?? null,
      address: input.address ?? null,
      city: input.city ?? null,
      phone: input.phone ?? null,
      fax: input.fax ?? null,
      email: input.email ?? null,
      carRegistrationNumber: input.carRegistrationNumber ?? null,
      defaultDiscountPct: input.defaultDiscountPct ?? 0,
      paymentTermsDays: input.paymentTermsDays ?? 0,
      isDefault: input.isDefault ?? false,
    } as Client),
    // Insert the optimistic row into every cached client list so the
    // user sees it immediately, even when offline (where the auto-
    // refetch can't run). On the online path, useResilientMutation
    // also invalidates CLIENTS_KEY which triggers a refetch — that
    // brings back the server-confirmed row and replaces this one.
    applyOptimistic: (qc, newClient) => {
      qc.setQueriesData<Client[]>({ queryKey: CLIENTS_KEY }, (old) => {
        if (!Array.isArray(old)) return old;
        // Dedupe by id in case applyOptimistic fires twice (StrictMode).
        if (old.some((c) => c.id === newClient.id)) return old;
        return [...old, newClient].sort((a, b) => a.name.localeCompare(b.name));
      });
    },
  });
}

export function useUpdateClient() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useResilientMutation<{ id: string; input: UpdateClientInput }, Client>({
    entity: 'clients',
    op: 'update',
    invalidate: [CLIENTS_KEY],
    online: ({ id, input }) => ds.clients.update(id, input),
    // CRITICAL: optimistic update MUST merge with the previously-cached
    // row, not replace it. Otherwise fields the form didn't touch
    // (taxId, fax, etc.) come back as null/empty and a form that re-binds
    // to the response shows blank inputs — exactly the "save → blank
    // form" bug users were hitting.
    optimistic: ({ id, input }) => {
      const cached = readCachedClient(qc, id);
      return { ...cached, ...input, id, updatedAt: nowIso() } as Client;
    },
    applyOptimistic: (qc, updated, { id }) => {
      qc.setQueriesData<Client[]>({ queryKey: CLIENTS_KEY }, (old) =>
        Array.isArray(old) ? old.map((c) => (c.id === id ? updated : c)) : old,
      );
      qc.setQueryData<Client>([...CLIENTS_KEY, id], updated);
    },
  });
}

function readCachedClient(qc: ReturnType<typeof useQueryClient>, id: string): Partial<Client> {
  // Try the per-id detail cache first; fall back to scanning every list
  // cache (filters + storefront + per-tenant variants) for the row.
  const direct = qc.getQueryData<Client>([...CLIENTS_KEY, id]);
  if (direct) return direct;
  for (const [, list] of qc.getQueriesData<Client[]>({ queryKey: CLIENTS_KEY })) {
    if (Array.isArray(list)) {
      const found = list.find((c) => c.id === id);
      if (found) return found;
    }
  }
  return { id };
}

export function useDeleteClient() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'clients',
    op: 'delete',
    invalidate: [CLIENTS_KEY],
    online: (id) => ds.clients.delete(id),
    optimistic: () => undefined as unknown as void,
    // Yank the row out of every cached list immediately.
    applyOptimistic: (qc, _result, id) => {
      qc.setQueriesData<Client[]>({ queryKey: CLIENTS_KEY }, (old) =>
        Array.isArray(old) ? old.filter((c) => c.id !== id) : old,
      );
      qc.removeQueries({ queryKey: [...CLIENTS_KEY, id] });
    },
  });
}
